import { useEffect, useState } from "react";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { Button } from "@/react/components/shared/uikit/Button";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import {
  useCommunitySettingShow,
  useUpdateCommunitySetting,
} from "@/react/hooks/UseCommunitySetting";
import { useControlsDropdownContext } from "./context";

const i18nBase = "members_directory.controls_dropdown.visibility_selector";

const activeClassName = "!bg-secondary !border-secondary";

export const VisibilitySelector = () => {
  const { data, isLoading } = useCommunitySettingShow();

  const updateCommunitySettingMutation = useUpdateCommunitySetting();

  const { setCurrentComponent } = useControlsDropdownContext();

  const communitySetting = data?.community_settings;

  const [isPublic, setIsPublic] = useState<boolean>();

  useEffect(() => {
    const isPublicMemberDirectory = Boolean(
      communitySetting?.public_member_directory,
    );
    setIsPublic(isPublicMemberDirectory);
  }, [communitySetting]);

  const handleSave = () => {
    updateCommunitySettingMutation.mutate(
      {
        data,
        updates: { public_member_directory: isPublic },
      },
      {
        onSuccess: () => {
          setCurrentComponent("root");
        },
      },
    );
  };

  const handleCancel = () => {
    setIsPublic(Boolean(communitySetting?.public_member_directory));
    setCurrentComponent("root");
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <Typography.LabelMd weight="semibold">
        {t(`${i18nBase}.title`)}
      </Typography.LabelMd>
      {isLoading ? (
        <div className="flex min-h-32 items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          <div className="mt-2 flex justify-between">
            <Button
              variant="secondary-light"
              className={classNames(
                "flex items-center gap-2 rounded-xl !px-5 !py-2.5",
                {
                  [activeClassName]: isPublic,
                },
              )}
              onClick={() => setIsPublic(true)}
            >
              <Icon type="20-globe-v2" size={20} />
              <Typography.LabelSm weight="medium" color="text-darkest">
                {t(`${i18nBase}.public`)}
              </Typography.LabelSm>
            </Button>

            <Button
              variant="secondary-light"
              className={classNames(
                "flex items-center gap-2 rounded-xl !px-5 !py-2.5",
                {
                  [activeClassName]: !isPublic,
                },
              )}
              onClick={() => setIsPublic(false)}
            >
              <Icon type="20-lock" size={20} />
              <Typography.LabelSm weight="medium" color="text-darkest">
                {t(`${i18nBase}.private`)}
              </Typography.LabelSm>
            </Button>
          </div>

          <div className="min-h-16">
            <Typography.BodySm>
              {t(`${i18nBase}.description.${isPublic ? "public" : "private"}`)}
            </Typography.BodySm>
          </div>
        </>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={handleCancel}>
          {t(`${i18nBase}.cancel`)}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={updateCommunitySettingMutation.isLoading}
        >
          {t(`${i18nBase}.save`)}
        </Button>
      </div>
    </div>
  );
};
